import React, { useCallback, useState } from 'react';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { useAccountNumber } from '../../fetchHooks/useAccount';
import useAccountChange from '../../fetchHooks/useAccountChange';
import { usePaymentItemDetails } from '../../fetchHooks/usePaymentItems';
import useShowPaymentResult from '../../hooks/useShowPaymentResult';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { FormattedFuturePayment } from '../../utils/mapper/futurepaymentsUtil';
import FuturePaymentDetails from './FuturePaymentDetails';
import FuturePaymentModal from './FuturePaymentModal';

import './FuturePayment.scss';

interface Props {
    futurePayment: FormattedFuturePayment;
    onClose: () => void;
}

const FuturePayment: React.FC<Props> = ({ futurePayment, onClose }) => {
    const refresh = useAccountChange();

    const { signingCompleted, dispatch, showSigningFlow } = useSigningContext();

    const { data: account } = useAccountNumber(futurePayment.fromAccount.accountNumber);
    const [openEdit, setOpenEdit] = useState(false);
    const [hidePaymentButton, setHidePaymentButton] = useState(false);

    const handleClose = useCallback(() => {
        if (signingCompleted) {
            refresh(account?.accountId ? [account?.accountId] : []);
        }
        dispatch({ type: Actions.RESET });
        onClose();
    }, [signingCompleted, dispatch, onClose, refresh, account?.accountId]);

    const showPaymentResult = useShowPaymentResult();
    const hideCloseButton = showSigningFlow && !showPaymentResult;

    const { data: futurePaymentDetails, isLoading } = usePaymentItemDetails(
        { value: futurePayment.id },
        { enabled: futurePayment.isFaked !== true },
    );

    return (
        <FuturePaymentModal
            futurePaymentDetails={{ data: futurePaymentDetails, isLoading }}
            futurePayment={futurePayment}
            openEditModalState={[openEdit, setOpenEdit]}
            paymentButtonState={[hidePaymentButton, setHidePaymentButton]}
            onClose={handleClose}
            hideCloseButton={hideCloseButton}
        >
            <FuturePaymentDetails
                onClose={() => setOpenEdit(false)}
                futurePayment={futurePayment}
                account={account}
                hidePaymentButton={{ value: hidePaymentButton, set: setHidePaymentButton }}
            />
        </FuturePaymentModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.PAYMENT}>
        <FuturePayment {...props} />
    </SigningContextProvider>
);
